"use client";

import {
	type KeyboardEvent,
	type PointerEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import {
	LEFT_PANEL_WIDTH,
	LEFT_PANEL_WIDTH_STORAGE_KEY,
	clampLeftPanelWidth as clampWidth,
	setLeftPanelWidthVar,
} from "../lib/left-panel-width";

const KEYBOARD_STEP = 20;

/** 布局宽度由 <html> 上的 CSS 变量决定（首帧前脚本写好）；这里的 state 只给拖动起点和 aria-valuenow 用 */
export function useLeftPanelWidth() {
	const [width, setWidth] = useState<number>(LEFT_PANEL_WIDTH.default);

	useEffect(() => {
		try {
			const saved = Number(
				localStorage.getItem(LEFT_PANEL_WIDTH_STORAGE_KEY),
			);
			if (saved) {
				setWidth(clampWidth(saved));
			}
		} catch {
			// 隐私模式等读不到就用默认宽度
		}
	}, []);

	const commit = useCallback((next: number) => {
		setWidth(next);
		setLeftPanelWidthVar(next);
		try {
			localStorage.setItem(LEFT_PANEL_WIDTH_STORAGE_KEY, String(next));
		} catch {
			// 存不下就只在本次会话生效
		}
	}, []);

	return { width, commit };
}

/**
 * 左右栏之间的竖线 + 拖动柄。拖动中直接改 <html> 上的 CSS 变量，松手才 commit：
 * 工作台组件很大，每个 pointermove 都 setState 会拖得一卡一卡的。
 */
export function PanelResizer({
	width,
	onCommit,
	label,
}: {
	width: number;
	onCommit: (width: number) => void;
	label: string;
}) {
	const drag = useRef<{
		startX: number;
		startWidth: number;
		current: number;
	}>(null);
	const [dragging, setDragging] = useState(false);

	const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
		if (event.button !== 0) {
			return;
		}
		event.preventDefault();
		event.currentTarget.setPointerCapture(event.pointerId);
		drag.current = {
			startX: event.clientX,
			startWidth: width,
			current: width,
		};
		setDragging(true);
	};

	const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
		if (!drag.current) {
			return;
		}
		const next = clampWidth(
			drag.current.startWidth + event.clientX - drag.current.startX,
		);
		drag.current.current = next;
		setLeftPanelWidthVar(next);
	};

	const endDrag = (event: PointerEvent<HTMLDivElement>) => {
		if (!drag.current) {
			return;
		}
		if (event.currentTarget.hasPointerCapture(event.pointerId)) {
			event.currentTarget.releasePointerCapture(event.pointerId);
		}
		onCommit(drag.current.current);
		drag.current = null;
		setDragging(false);
	};

	const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		const step =
			event.key === "ArrowLeft"
				? -KEYBOARD_STEP
				: event.key === "ArrowRight"
					? KEYBOARD_STEP
					: 0;
		if (step) {
			event.preventDefault();
			onCommit(clampWidth(width + step));
		}
	};

	return (
		<div className="relative hidden w-px flex-none bg-border studio-split:block">
			{/* biome-ignore lint/a11y/useSemanticElements: 可拖的分隔条（ARIA window splitter）要能聚焦、接指针事件，<hr> 做不到 */}
			<div
				role="separator"
				aria-orientation="vertical"
				aria-label={label}
				aria-valuemin={LEFT_PANEL_WIDTH.min}
				aria-valuemax={LEFT_PANEL_WIDTH.max}
				aria-valuenow={width}
				tabIndex={0}
				onPointerDown={onPointerDown}
				onPointerMove={onPointerMove}
				onPointerUp={endDrag}
				onPointerCancel={endDrag}
				onKeyDown={onKeyDown}
				data-dragging={dragging || undefined}
				className="group absolute inset-y-0 -left-2 z-10 flex w-[17px] cursor-col-resize touch-none select-none items-center justify-center outline-none"
			>
				{/* 命中区 17px 宽，看得见的只有中间那条线和一串点 */}
				<span className="absolute inset-y-0 left-2 w-px bg-transparent transition-colors group-hover:bg-primary/40 group-focus-visible:bg-primary group-data-[dragging]:bg-primary/60" />
				<span className="relative flex flex-col gap-[3px] rounded-full bg-background py-1">
					{[0, 1, 2, 3, 4].map((dot) => (
						<i
							key={dot}
							className="block size-[3px] rounded-full bg-muted-foreground/50 group-hover:bg-primary group-data-[dragging]:bg-primary"
						/>
					))}
				</span>
			</div>
		</div>
	);
}
